import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Vote, Search, Loader2, Plus, X, ShieldCheck, Copy, MapPin } from "lucide-react";
import { sr } from "../../lib/sr";
import AudienceBrowser from "../../components/smartreach/AudienceBrowser";

const MEDIA = [["ctv", "CTV / Streaming TV"], ["video", "Online video"], ["audio", "Audio / podcast"], ["display", "Display"]];

const KIND_BADGE = {
  district: ["District", "sr-badge-blue"], state: ["State", "sr-badge-gray"],
  county: ["County", "sr-badge-gray"], dma: ["DMA", "sr-badge-gray"], geo: ["Geo", "sr-badge-gray"],
};

// Static, editable compliant-messaging tone presets (placeholders: {candidate} {office} {district}).
const DEFAULT_PRESETS = [
  { id: "intro", title: "Introduce the candidate", body: "Meet {candidate} — running for {office} in {district}. A proven leader who will fight for our community. Learn more and get involved today." },
  { id: "gotv", title: "Get out the vote (GOTV)", body: "Your vote decides {district}. Polls are open — make a plan to vote for {candidate} for {office}. Every vote counts." },
  { id: "contrast", title: "Issue contrast", body: "On the issues that matter to {district}, {candidate} will deliver for {office}. Compare the records and decide for yourself." },
  { id: "endorse", title: "Endorsement / testimonial", body: "Local leaders trust {candidate} for {office}. See why {district} families are backing {candidate} this election." },
];

function Section({ n, title, desc, children, testid }) {
  return (
    <div className="sr-card" style={{ padding: 16, marginBottom: 16 }} data-testid={testid}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <span style={{ width: 26, height: 26, borderRadius: 999, background: "var(--navy)", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>{n}</span>
        <strong style={{ color: "var(--navy)", fontSize: 16 }}>{title}</strong>
      </div>
      {desc && <p className="sr-muted" style={{ fontSize: 12.5, margin: "0 0 12px 36px" }}>{desc}</p>}
      <div style={{ marginLeft: 36 }}>{children}</div>
    </div>
  );
}

export default function PoliticalStudio() {
  const [dq, setDq] = useState("");
  const [dres, setDres] = useState([]);
  const [dbusy, setDbusy] = useState(false);
  const [districts, setDistricts] = useState([]);

  // compliance
  const [committee, setCommittee] = useState("");
  const [candidate, setCandidate] = useState("");
  const [office, setOffice] = useState("");
  const [authorized, setAuthorized] = useState(true);
  const [medium, setMedium] = useState("ctv");
  const [disc, setDisc] = useState(null);
  const [cbusy, setCbusy] = useState(false);
  const [rules, setRules] = useState([]);
  const [presets, setPresets] = useState(DEFAULT_PRESETS);

  useEffect(() => {
    searchDistricts("");
    sr.complianceRules().then((r) => setRules(r.rules || r || [])).catch(() => {});
  }, []); // eslint-disable-line

  const searchDistricts = async (qv) => {
    setDbusy(true);
    try { const r = await sr.listDistricts(qv, 24); setDres(r.items || []); }
    catch { toast.error("Could not load districts"); } finally { setDbusy(false); }
  };
  const addDistrict = (it) => {
    if (districts.some((d) => d.id === it.id)) return;
    setDistricts((p) => [...p, it]); toast.success(`Added ${it.name}`);
  };
  const removeDistrict = (id) => setDistricts((p) => p.filter((d) => d.id !== id));

  const buildDisclaimer = async () => {
    if (!committee.trim()) { toast.message("Enter the committee name (the 'Paid for by' entity)."); return; }
    setCbusy(true);
    try {
      const r = await sr.buildDisclaimer({ committee_name: committee.trim(), medium, authorized_by_candidate: authorized, candidate_name: candidate.trim() || undefined });
      setDisc(r);
    } catch { toast.error("Could not build disclaimer"); } finally { setCbusy(false); }
  };

  const fill = (body) => body.replace(/\{candidate\}/g, candidate.trim() || "[candidate]")
    .replace(/\{office\}/g, office.trim() || "[office]")
    .replace(/\{district\}/g, districts[0]?.name || "[district]");
  const copy = (text) => { try { navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.message("Copy not available"); } };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "8px 4px 40px" }} data-testid="political-studio-page">
      <h1 style={{ color: "var(--navy)", fontSize: 26, margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}><Vote size={22} /> Political Studio</h1>
      <p className="sr-muted" style={{ marginTop: 6, marginBottom: 16 }}>A guided TotalReach flow — target real districts, build your audience plan, and generate compliant messaging in three steps.</p>

      {/* Step 1 — District targeting */}
      <Section n={1} title="District targeting" desc="Search real congressional districts, DMAs, counties and states straight from the catalogue." testid="ps-districts">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          <input className="sr-input" value={dq} onChange={(e) => setDq(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") searchDistricts(dq.trim()); }}
            placeholder="e.g. Texas, TX-23, Philadelphia DMA, Maricopa County" data-testid="district-search-input" style={{ minWidth: 300 }} />
          <button className="sr-btn sr-btn-sm sr-btn-gold" onClick={() => searchDistricts(dq.trim())} disabled={dbusy} data-testid="district-search-btn">
            {dbusy ? <Loader2 size={14} className="sr-spin" /> : <Search size={14} />} Search
          </button>
        </div>

        {districts.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }} data-testid="selected-districts">
            {districts.map((d) => (
              <span key={d.id} className="sr-badge sr-badge-blue" style={{ fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <MapPin size={11} /> {d.name}
                <button onClick={() => removeDistrict(d.id)} data-testid={`district-remove-${d.id}`} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0, display: "inline-flex" }}><X size={12} /></button>
              </span>
            ))}
          </div>
        )}

        <div style={{ display: "grid", gap: 8 }}>
          {dbusy && !dres.length && <div className="sr-muted" style={{ fontSize: 13 }}><Loader2 size={13} className="sr-spin" /> Loading districts…</div>}
          {dres.map((it) => {
            const [kl, kc] = KIND_BADGE[it.kind] || KIND_BADGE.geo;
            const added = districts.some((d) => d.id === it.id);
            return (
              <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 10 }} data-testid={`district-row-${it.id}`}>
                <span className={`sr-badge ${kc}`} style={{ fontSize: 10 }}>{kl}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "var(--navy)", fontWeight: 700, fontSize: 13 }}>{it.name}</div>
                  <div className="sr-muted" style={{ fontSize: 11.5 }}>{it.audience_size_label} reach{it.instant ? " · Instant" : ""}</div>
                </div>
                <button className="sr-btn sr-btn-sm" onClick={() => addDistrict(it)} disabled={added} data-testid={`district-add-${it.id}`} style={{ background: added ? "#E8F0E8" : "#fff" }}>
                  {added ? "Added" : <><Plus size={13} /> Add</>}
                </button>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Step 2 — Audiences */}
      <Section n={2} title="Audiences & plan" desc="Shortlist political affinity & issue audiences, estimate reach by budget and frequency, compare and allocate, then export the plan." testid="ps-audiences">
        <AudienceBrowser industry="political" industryLabel="Political (TotalReach)" defaultBudget={10000} />
      </Section>

      {/* Step 3 — Compliant messaging */}
      <Section n={3} title="Compliant messaging presets" desc="Auto-build the 'Paid for by' disclaimer with medium-specific requirements, plus ready-to-use message tone presets." testid="ps-compliance">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          <div>
            <label className="sr-eyebrow">Committee (Paid for by)</label>
            <input className="sr-input" value={committee} onChange={(e) => setCommittee(e.target.value)} placeholder="Friends of Jane Doe" data-testid="committee-input" />
          </div>
          <div>
            <label className="sr-eyebrow">Candidate (optional)</label>
            <input className="sr-input" value={candidate} onChange={(e) => setCandidate(e.target.value)} placeholder="Jane Doe" data-testid="candidate-input" />
          </div>
          <div>
            <label className="sr-eyebrow">Office (for presets)</label>
            <input className="sr-input" value={office} onChange={(e) => setOffice(e.target.value)} placeholder="State Senate, District 12" data-testid="office-input" />
          </div>
          <div>
            <label className="sr-eyebrow">Medium</label>
            <select className="sr-select" value={medium} onChange={(e) => setMedium(e.target.value)} data-testid="medium-select">
              {MEDIA.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--navy)", marginBottom: 12, cursor: "pointer" }}>
          <input type="checkbox" checked={authorized} onChange={(e) => setAuthorized(e.target.checked)} data-testid="authorized-checkbox" />
          Authorized by the candidate / candidate's committee
        </label>
        <div>
          <button className="sr-btn sr-btn-sm sr-btn-gold" onClick={buildDisclaimer} disabled={cbusy} data-testid="build-disclaimer-btn">
            {cbusy ? <Loader2 size={14} className="sr-spin" /> : <ShieldCheck size={14} />} Build disclaimer
          </button>
        </div>

        {disc && (
          <div style={{ marginTop: 12, padding: 14, background: "var(--navy)", color: "#fff", borderRadius: 12 }} data-testid="disclaimer-result">
            <div className="sr-eyebrow" style={{ color: "#cbd5e1" }}>Disclaimer</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
              <strong style={{ fontSize: 16 }} data-testid="disclaimer-text">{disc.disclaimer_text}</strong>
              <button className="sr-btn sr-btn-sm" onClick={() => copy(disc.disclaimer_text)} data-testid="copy-disclaimer" style={{ background: "#fff" }}><Copy size={12} /> Copy</button>
            </div>
            {(disc.requirements || []).length > 0 && (
              <ul style={{ margin: "10px 0 0", paddingLeft: 18, fontSize: 12.5, color: "#e2e8f0" }}>
                {disc.requirements.map((r, i) => <li key={i} style={{ marginBottom: 3 }} data-testid={`disclaimer-req-${i}`}>{r}</li>)}
              </ul>
            )}
          </div>
        )}

        {rules.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div className="sr-eyebrow" style={{ marginBottom: 6 }}>Compliance rules reference</div>
            <div style={{ display: "grid", gap: 8 }}>
              {rules.slice(0, 4).map((r, i) => (
                <div key={r.key || i} style={{ padding: 10, border: "1px solid var(--border)", borderRadius: 10 }} data-testid={`compliance-rule-${i}`}>
                  <strong style={{ color: "var(--navy)", fontSize: 13 }}>{r.title}</strong>
                  <p className="sr-muted" style={{ fontSize: 12, margin: "4px 0 0" }}>{r.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <div className="sr-eyebrow" style={{ marginBottom: 6 }}>Message tone presets (edit & copy)</div>
          <div style={{ display: "grid", gap: 10 }}>
            {presets.map((p, idx) => (
              <div key={p.id} style={{ padding: 10, border: "1px solid var(--border)", borderRadius: 10 }} data-testid={`preset-${p.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <strong style={{ color: "var(--navy)", fontSize: 13 }}>{p.title}</strong>
                  <button className="sr-btn sr-btn-sm" onClick={() => copy(fill(p.body))} data-testid={`preset-copy-${p.id}`} style={{ marginLeft: "auto", background: "#F3F5F8" }}><Copy size={12} /> Copy filled</button>
                </div>
                <textarea className="sr-input" value={p.body} rows={2}
                  onChange={(e) => setPresets((prev) => prev.map((x, i) => (i === idx ? { ...x, body: e.target.value } : x)))}
                  data-testid={`preset-body-${p.id}`} style={{ width: "100%", resize: "vertical", fontSize: 12.5 }} />
                <div className="sr-muted" style={{ fontSize: 11, marginTop: 4 }}>Preview: {fill(p.body)}</div>
              </div>
            ))}
          </div>
          <p className="sr-muted" style={{ fontSize: 11, marginTop: 8 }}>Presets are starting points — always run creatives through human compliance review and confirm the strictest applicable state disclosure before trafficking.</p>
        </div>
      </Section>
    </div>
  );
}
